// CONCEPT: owl.carousel, downloaded on first use. The plugin registers itself on $.fn when its
// module evaluates, so there is nothing to initialise - only the download to defer.
//
// Why lazy rather than deleted: `.owlCarousel(` appears nowhere in app/Views or src/js, and the
// markup class `.owl-carousel` appears nowhere either, so on this codebase it is 44KB of JS and
// 5KB of CSS on every page for nothing. But per-site content lives in
// public/local_resources/<member_code>/, which is deployed separately and is not in this repo,
// so "nothing calls it" cannot be proven for a real distributor site.
//
// Why a STUB on $.fn and not a DOM check in the ready handler, which is what this file did first
// and what was wrong: layout.inc loads local_resources/<member>/custom.min.js as a classic
// script near the bottom of the body (~line 1209), so it runs DURING PARSE and registers its
// `$(function () { $('.owl-carousel').owlCarousel({...}) })` before main.js - a deferred module -
// evaluates and registers readyPage. jQuery fires ready handlers in registration order, so the
// site's runs first. On main that was fine: the static `import 'owl.carousel'` had already
// attached $.fn.owlCarousel during module evaluation. Behind a DOM check in readyPage the plugin
// is still absent at that point, the site's call throws "owlCarousel is not a function", and
// nothing replays it - breaking the exact separately-deployed content the laziness was meant to
// preserve. A site calling it on any selector other than `.owl-carousel` never even started the
// import.
//
// So the METHOD is the trigger, not a class being in the DOM - the same contract shape as
// window.onDataTablesReady in data-tables.js. A synchronous stub is installed when this module
// evaluates, the first call to it starts the download, and the call is replayed onto the same
// elements once the real plugin has replaced the stub.
//
// The one thing this cannot preserve is a synchronous follow-up on the same tick: a site that
// calls .owlCarousel() and then immediately .trigger('next.owl.carousel') fires that trigger
// before the plugin has bound its handlers, and it is lost. Every known caller configures and
// stops, and the alternative is shipping the library to all 38 routes again.
//
// USE: the stub installs itself on import (main.js imports this statically). initOwlCarousel()
// from main.js's ready handler warms the chunk when carousel markup is on the page.
import $ from 'jquery';

let loadPromise = null;

function loadOwl() {
    // jquery-compat.js restores $.camelCase, which owl 2.3.4 calls from _trigger(). It is
    // imported at the top of main.js, so it is installed long before anything reaches this.
    loadPromise ??= Promise.all([
        import('owl.carousel'),
        import('owl.carousel/dist/assets/owl.carousel.min.css'),
        import('owl.carousel/dist/assets/owl.theme.default.min.css'),
    ]).catch(function (err) {
        console.error('[owl.carousel] failed to load', err);
    });

    return loadPromise;
}

/**
 * Put a placeholder $.fn.owlCarousel in place that loads the real plugin and replays the call.
 * Returns false when there is nothing to install onto, or when the real plugin is already there.
 */
export function installOwlCarouselStub() {
    if (typeof window === 'undefined' || !window.jQuery || !window.jQuery.fn) {
        return false;
    }

    var jq = window.jQuery;

    if (typeof jq.fn.owlCarousel === 'function') {
        return false;
    }

    function owlCarouselStub() {
        var $elements = this;
        var args = arguments;

        loadOwl().then(function () {
            // Importing the plugin overwrites this function. If the import FAILED it did not,
            // and replaying would call the stub again forever.
            if (jq.fn.owlCarousel !== owlCarouselStub) {
                jq.fn.owlCarousel.apply($elements, args);
            }
        });

        // owl returns the jQuery object for chaining; a caller doing `.owlCarousel().addClass()`
        // must not break while the chunk is in flight.
        return this;
    }

    owlCarouselStub.paceOwlCarouselStub = true;
    jq.fn.owlCarousel = owlCarouselStub;

    return true;
}

export function initOwlCarousel() {
    // Not the trigger - the stub above is. This only warms the chunk when carousel markup is
    // already on the page, so a call made later (from a click handler, say) does not wait.
    if (!$('.owl-carousel').length) return;

    return loadOwl();
}

installOwlCarouselStub();
